import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { cancelTicket, lookupCancelTicket } from "./ticketOpsApi";
import TicketStatusBadge from "./TicketStatusBadge";
import { getHostedTicketEventDetails } from "./hostedTicketEvents";
import "./rockin-road-home.css";

function attendeeName(ticket) {
  return [ticket?.attendee_first_name, ticket?.attendee_last_name].filter(Boolean).join(" ");
}

function formatEventDate(ticket) {
  if (!ticket?.event_date_utc) return "";
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeZone: ticket.event_timezone || "America/Chicago",
  }).format(new Date(ticket.event_date_utc));
}

function getCancelState(data) {
  const ticketStatus = data?.ticket?.ticket_status;
  if (data?.found === false) return "not_found";
  if (!data || data?.ok === false || !data?.ticket) return "unavailable";
  if (data?.already_cancelled || ticketStatus === "cancelled") return "cancelled";
  if (ticketStatus === "checked_in") return "checked_in";
  if (ticketStatus === "void") return "void";
  if (data?.cancelled) return "cancelled";
  return "active";
}

export default function TicketCancelPage() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);
  const [message, setMessage] = useState("");
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [actionStatus, setActionStatus] = useState("idle");

  useEffect(() => {
    let cancelled = false;

    async function loadTicket() {
      setStatus("loading");
      setMessage("");
      const response = await lookupCancelTicket(token);
      if (cancelled) return;

      setData(response);
      setStatus("loaded");
      if (response?.ok === false && response?.error) {
        setMessage(response.error);
      }
    }

    loadTicket();

    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleCancel() {
    if (!confirmed || actionStatus === "submitting") return;

    setActionStatus("submitting");
    setMessage("");
    const response = await cancelTicket(token, reason);
    setData(response);
    setActionStatus("idle");

    if (response?.ok === false && response?.error) {
      setMessage(response.error);
    } else if (response?.cancelled) {
      setMessage("This ticket has been cancelled.");
    }
  }

  const ticket = data?.ticket;
  const eventDetails = getHostedTicketEventDetails(ticket?.event_id);
  const cancelState = getCancelState(data);
  const canCancel = cancelState === "active" && confirmed && actionStatus !== "submitting";

  return (
    <main className="rrh-page rrh-ops-page">
      <section className="rrh-ops-panel" aria-labelledby="rrh-ticket-cancel-heading">
        <div>
          <p className="rrh-section__eyebrow">Ticket cancellation</p>
          <h1 id="rrh-ticket-cancel-heading">Cancel Ticket</h1>
        </div>

        {status === "loading" ? (
          <p className="rrh-state-message">Loading ticket...</p>
        ) : (
          <>
            {message && (
              <p className={`rrh-state-message ${data?.ok === false ? "rrh-state-message--error" : "rrh-state-message--success"}`}>
                {message}
              </p>
            )}
            {cancelState === "unavailable" && !message && (
              <p className="rrh-state-message rrh-state-message--error">Unable to load this ticket.</p>
            )}
            {cancelState === "not_found" && (
              <p className="rrh-state-message rrh-state-message--error">Ticket not found or this link has expired.</p>
            )}
            {cancelState === "cancelled" && !message && (
              <p className="rrh-state-message rrh-state-message--success">This ticket is already cancelled.</p>
            )}
            {cancelState === "checked_in" && (
              <p className="rrh-state-message rrh-state-message--warning">This ticket has already been checked in and cannot be cancelled online.</p>
            )}
            {cancelState === "void" && (
              <p className="rrh-state-message rrh-state-message--warning">This ticket is void and cannot be cancelled online.</p>
            )}

            {ticket && (
              <div className="rrh-ticket-summary">
                <dl>
                  <div>
                    <dt>Event</dt>
                    <dd>{ticket.event_name || "Road Home LIVE"}</dd>
                  </div>
                  {formatEventDate(ticket) && (
                    <div>
                      <dt>Date</dt>
                      <dd>{formatEventDate(ticket)}</dd>
                    </div>
                  )}
                  {eventDetails?.venue && (
                    <div>
                      <dt>Venue</dt>
                      <dd>{eventDetails.venue}<br />{eventDetails.address}</dd>
                    </div>
                  )}
                  <div>
                    <dt>Attendee</dt>
                    <dd>{attendeeName(ticket) || "Guest"}</dd>
                  </div>
                  <div>
                    <dt>Ticket code</dt>
                    <dd>{ticket.ticket_code || "Unavailable"}</dd>
                  </div>
                  <div>
                    <dt>Status</dt>
                    <dd><TicketStatusBadge status={ticket.ticket_status} /></dd>
                  </div>
                </dl>
              </div>
            )}

            {ticket && cancelState === "active" && (
              <form className="rrh-ops-form" onSubmit={(event) => event.preventDefault()}>
                <label className="rrh-field">
                  <span>Cancellation reason <em>optional</em></span>
                  <textarea
                    maxLength={500}
                    onChange={(event) => setReason(event.target.value)}
                    value={reason}
                  />
                </label>

                <label className="rrh-check">
                  <input
                    checked={confirmed}
                    onChange={(event) => setConfirmed(event.target.checked)}
                    type="checkbox"
                  />
                  <span>I understand this will cancel this ticket.</span>
                </label>

                <button
                  className="rrh-button rrh-button--primary"
                  disabled={!canCancel}
                  onClick={handleCancel}
                  type="button"
                >
                  {actionStatus === "submitting" ? "Cancelling..." : "Cancel This Ticket"}
                </button>
              </form>
            )}
          </>
        )}
      </section>
    </main>
  );
}
